<template>
  <div
    data-testid="page-festival-shop-layout"
    class="just sticky top-0 z-10 mt-16 w-full flex-col items-center laptop:mt-0"
    :class="{ 'h-[calc(64px+40px)]': showTopBarCallout, 'h-[calc(64px)]': !showTopBarCallout }"
  >
    <div class="flex w-full justify-between border-b border-main bg-surface px-6 py-2">
      <div class="flex items-center justify-center gap-2">
        <FestivalShopBreadcrumbs />
        <div></div>
      </div>
      <button v-if="hasSelectedKiosk && allowPurchasing" class="btn-outline-neutral-s" @click="showCart()">
        <ShoppingCartIcon class="lucide-icon-sm" />
        <span>{{ formatPrice(cartTotal, locale) }} </span>
        <ChipBadge v-if="cartItemCount > 0" color="purple" rounded size="m" type="solid">{{ cartItemCount }}</ChipBadge>
      </button>
    </div>
    <div
      v-if="showTopBarCallout"
      class="flex items-center gap-1 border border-x-0 border-t-0 border-warning-subtle bg-warning-subtle px-6 py-2 text-warning"
    >
      <Info class="lucide-icon-s" />
      <p class="txt-headline-s">{{ $t('FestivalShop.TopBar.callout') }}</p>
    </div>
  </div>
  <div
    class="w-screen max-w-[1200px] bg-surface-elevation-1 px-8 py-8 laptop:mx-auto laptop:w-full"
    :class="{ 'min-h-[calc(100vh-64px-40px)]': showTopBarCallout, 'min-h-[calc(100vh-64px)]': !showTopBarCallout }"
  >
    <RouterView />
  </div>

  <!-- Cart Modal -->
  <FestivalShopCart v-if="hasSelectedKiosk" :is-open="isCartOpen" :kiosk-name="kioskName" @close="closeCart()" @checkout="handleCheckout" />

  <!-- Navigation Confirmation Dialog -->
  <ConfirmModal
    v-if="showNavigationDialog"
    :title="t('FestivalShop.confirmNavigation.title')"
    :description="t('FestivalShop.confirmNavigation.description')"
    :cancel="t('FestivalShop.confirmNavigation.leave')"
    :approve="t('FestivalShop.confirmNavigation.stay')"
    wrapper-class="rounded-lg"
    @close-dialog="handleNavigationDialog"
  />
</template>
<script lang="ts" setup>
import { ChipBadge } from '@ramp106/omrjs-core-ui'
import { Info, ShoppingCartIcon } from '@ramp106/omrjs-core-ui/icons'
import { storeToRefs } from 'pinia'
import { computed, provide, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'

import ConfirmModal from '@/components/ConfirmModal.vue'
import { FestivalShopCart } from '@/components/FestivalShop/Cart'
import FestivalShopBreadcrumbs from '@/components/FestivalShop/FestivalShopBreadcrumbs.vue'
import { formatPrice } from '@/components/FestivalShop/helpers'
import { useFestivalShopCart, FestivalShopCartKey } from '@/composables/useFestivalShopCart'
import { festivalShopConfig } from '@/config/festivalShop'
import { useKioskByIdQuery, type MeQuery } from '@/gql/myomr'
import { showNotification } from '@/helpers/notificationHelper'
import { globalizeUrlifiedId } from '@/helpers/urlifyGlobalId'
import { useRoleRedirect } from '@/services/roleRedirect'
import { useLocaleStore } from '@/stores'

const route = useRoute()
const router = useRouter()
const { locale } = storeToRefs(useLocaleStore())

defineProps<{
  me: NonNullable<MeQuery['me']>
}>()

useRoleRedirect('viewFestivalShop')

const festivalShopCart = useFestivalShopCart()
provide(FestivalShopCartKey, festivalShopCart)

const { cartItems, cartItemCount, cartTotal, isCartOpen, closeCart, showCart } = festivalShopCart
const { t } = useI18n()
const { showTopBarCallout, allowPurchasing } = festivalShopConfig

// Kiosk query for dynamic kiosk name
const kioskId = computed(() => (route.params.kioskId as string | undefined) ?? null)
const kioskGlobalId = computed(() => (kioskId.value ? globalizeUrlifiedId(kioskId.value) : ''))
const hasSelectedKiosk = computed(() => Boolean(kioskId.value))

const kioskQueryVars = computed(() => ({
  kioskId: kioskGlobalId.value,
}))
const kioskQueryOptions = computed(() => ({
  enabled: hasSelectedKiosk.value,
}))

const { result: kioskDetailsResult } = useKioskByIdQuery(kioskQueryVars, kioskQueryOptions)
const kioskName = computed(() => {
  const kiosk = kioskDetailsResult.value?.kioskById?.kiosk
  return kiosk?.name ?? t('FestivalShop.StartPage.cartTitle')
})
const missingRequiredTimeslotCategoryNames = computed(() => {
  const categories = kioskDetailsResult.value?.kioskById?.productCategories ?? []
  const categoryIdsInCart = new Set(
    cartItems.value.map((item) => item.productCategoryId).filter((categoryId): categoryId is string => !!categoryId),
  )

  return categories
    .filter(
      (category) =>
        categoryIdsInCart.has(category.id) &&
        category.enableDeliveryTimeslots &&
        category.deliveryTimeslotSelectionRequired &&
        !category.kioskSelectedDeliverySlot,
    )
    .map((category) => category.name)
})

watch(hasSelectedKiosk, (selected) => {
  if (!selected) {
    closeCart()
  }
})

// Navigation confirmation state
const showNavigationDialog = ref(false)
const pendingNavigation = ref<(() => void) | null>(null)

function handleCheckout() {
  if (missingRequiredTimeslotCategoryNames.value.length > 0) {
    const categoryList = missingRequiredTimeslotCategoryNames.value.join(', ')
    showNotification(`${t('FestivalShop.products.details.disabled_not_configured')}: ${categoryList}`, 'error')
    return
  }
  closeCart()
  router.push({ name: 'shopCheckout' })
}

// Navigation guard - warn before leaving festival shop with items in cart
onBeforeRouteLeave((to) => {
  // Allow navigation if cart is empty
  if (cartItemCount.value === 0) {
    return true
  }

  // Allow navigation within festival shop
  if (to.path.startsWith('/festival-shop')) {
    return true
  }

  // Show confirmation dialog for external navigation
  showNavigationDialog.value = true
  pendingNavigation.value = () => {
    router.push(to)
  }
  return false
})

// Handle navigation dialog response
function handleNavigationDialog(confirmed: boolean) {
  showNavigationDialog.value = false
  if (confirmed && pendingNavigation.value) {
    // Clear cart and proceed with navigation
    // This is need otherwise the router-push will trigger again the dialog
    festivalShopCart.clearCart()
    pendingNavigation.value()
  }
  pendingNavigation.value = null
}

// Warn before page unload (refresh/close/back button)
const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  if (cartItemCount.value > 0) {
    e.preventDefault()
    e.returnValue = '' // Chrome requires returnValue to be set
  }
}

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})
</script>
